import React, { useMemo, useState } from 'react';
import { Check, Search, ChefHat, Sparkles, Coffee, Baby, Leaf } from 'lucide-react';
import { Input } from '../ui/input';

const AddonsSection = ({ 
  addonItems, 
  selectedAddons, 
  onToggleAddon,
  guestCount,
  isExpanded, 
  onToggle 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const isSelected = (itemId) => selectedAddons.find(a => a.id === itemId);

  const getAddonsTotal = () => {
    return selectedAddons.reduce((sum, item) => sum + (item.price_per_person || 0), 0);
  };

  // Enhanced categories for new add-ons
  const categories = [
    { key: 'all', label: 'All', icon: '📋' },
    { key: 'appetizers', label: 'Appetizers', icon: '🍤' },
    { key: 'stations', label: 'Action Stations', icon: '👨‍🍳' },
    { key: 'desserts', label: 'Desserts', icon: '🍰' },
    { key: 'beverages', label: 'Beverages', icon: '☕' },
    { key: 'upgrades', label: 'Upgrades', icon: '⭐' },
    { key: 'kids', label: 'Kids', icon: '👶' },
    { key: 'dietary', label: 'Dietary', icon: '🌱' },
    // Legacy categories
    { key: 'appetizer', label: 'Appetizers', icon: '🍤', hidden: true },
    { key: 'salad', label: 'Salads', icon: '🥗' },
    { key: 'side', label: 'Sides', icon: '🥔' },
    { key: 'main', label: 'Proteins', icon: '🍖' },
    { key: 'dessert', label: 'Desserts', icon: '🍰', hidden: true },
    { key: 'beverage', label: 'Beverages', icon: '☕', hidden: true }
  ];

  // Filter items
  const filteredItems = useMemo(() => {
    let items = addonItems;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(i => 
        i.name?.toLowerCase().includes(query) ||
        i.description?.toLowerCase().includes(query)
      );
    }
    
    if (activeCategory !== 'all') {
      items = items.filter(i => i.sub_category === activeCategory);
    }
    
    return items;
  }, [addonItems, searchQuery, activeCategory]);

  // Group by sub_category
  const groupedItems = useMemo(() => {
    const groups = {};
    filteredItems.forEach(item => {
      const cat = item.sub_category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [filteredItems]);

  // Get counts for each category
  const categoryCounts = useMemo(() => {
    const counts = { all: addonItems.length };
    addonItems.forEach(item => {
      const cat = item.sub_category || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [addonItems]);

  const getCategoryLabel = (key) => {
    const cat = categories.find(c => c.key === key);
    return cat ? cat.label : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
  };

  const getCategoryIcon = (key) => {
    const cat = categories.find(c => c.key === key);
    return cat?.icon || '📦';
  };

  return (
    <div className="border-b pb-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left font-semibold text-[#1e3a5f] mb-3"
        data-testid="addons-section-toggle"
      >
        <span className="flex items-center gap-2">
          ➕ Add-Ons
          {selectedAddons.length > 0 && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({selectedAddons.length} items - ${getAddonsTotal().toFixed(2)}/person)
            </span>
          )}
        </span>
        <span className="text-sm text-gray-500">{isExpanded ? '−' : '+'}</span>
      </button>
      
      {isExpanded && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <p className="text-sm text-gray-600">Add extra items to your menu at per-person pricing</p>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Search add-ons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm bg-white"
              data-testid="addon-search"
            />
          </div>

          {/* Category tabs */}
          <div className="flex flex-wrap gap-1">
            {categories.filter(c => !c.hidden && categoryCounts[c.key] > 0).map(({ key, label, icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveCategory(key)}
                className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                  activeCategory === key 
                    ? 'bg-[#c9a227] text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span>{icon}</span>
                <span>{label}</span>
                <span className="opacity-70">({categoryCounts[key] || 0})</span>
              </button>
            ))}
          </div>

          {/* Selected summary */}
          {selectedAddons.length > 0 && (
            <div className="bg-[#c9a227]/10 p-2 rounded-lg">
              <p className="text-xs font-medium text-[#8b7355] mb-1">Selected ({selectedAddons.length}):</p>
              <div className="flex flex-wrap gap-1">
                {selectedAddons.slice(0, 6).map(item => (
                  <span key={item.id} className="text-xs bg-white px-2 py-0.5 rounded flex items-center gap-1">
                    {item.name}
                    <span className="text-[#1e3a5f] font-semibold">${item.price_per_person?.toFixed(2)}</span>
                  </span>
                ))}
                {selectedAddons.length > 6 && (
                  <span className="text-xs text-gray-500">+{selectedAddons.length - 6} more</span>
                )}
              </div>
            </div>
          )}

          {/* Items list */}
          <div className="max-h-80 overflow-y-auto space-y-4">
            {activeCategory === 'all' ? (
              // Show grouped by category
              Object.entries(groupedItems).map(([cat, items]) => (
                <div key={cat}>
                  <h4 className="font-medium text-[#8b7355] mb-2 flex items-center gap-2 text-sm">
                    <span>{getCategoryIcon(cat)}</span>
                    <span>{getCategoryLabel(cat)}</span>
                    <span className="text-xs text-gray-400">({items.length})</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {items.map((item) => {
                      const selected = isSelected(item.id);
                      return (
                        <div
                          key={item.id}
                          onClick={() => onToggleAddon(item)}
                          data-testid={`addon-${item.id}`}
                          className={`p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                            selected
                              ? 'border-[#c9a227] bg-[#c9a227] text-white'
                              : 'border-gray-200 bg-white hover:border-[#c9a227]'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {selected && <Check size={14} className="flex-shrink-0" />}
                              <span className="truncate">{item.name}</span>
                            </div>
                            <span className="font-bold ml-2 flex-shrink-0">
                              {item.price_per_person > 0 ? `$${item.price_per_person?.toFixed(2)}` : 'Free'}
                            </span>
                          </div>
                          {item.description && !selected && (
                            <p className="text-xs text-gray-500 mt-1 truncate">{item.description}</p>
                          )}
                          {item.requires_attendant && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded mt-1 inline-block">
                              Requires attendant
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              // Show flat list for filtered category
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {filteredItems.map((item) => {
                  const selected = isSelected(item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => onToggleAddon(item)}
                      data-testid={`addon-${item.id}`}
                      className={`p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                        selected
                          ? 'border-[#c9a227] bg-[#c9a227] text-white'
                          : 'border-gray-200 bg-white hover:border-[#c9a227]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {selected && <Check size={14} className="flex-shrink-0" />}
                          <span className="truncate">{item.name}</span>
                        </div>
                        <span className="font-bold ml-2 flex-shrink-0">
                          {item.price_per_person > 0 ? `$${item.price_per_person?.toFixed(2)}` : 'Free'}
                        </span>
                      </div>
                      {item.description && !selected && (
                        <p className="text-xs text-gray-500 mt-1 truncate">{item.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {filteredItems.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-6">
                {searchQuery ? 'No items match your search' : 'No add-on items available'}
              </p>
            )}
          </div>
          
          {/* Total */}
          {selectedAddons.length > 0 && guestCount > 0 && (
            <div className="mt-2 p-3 bg-white rounded-lg border border-[#c9a227]">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Add-Ons Subtotal ({guestCount} guests):</span>
                <span className="font-bold text-[#1e3a5f]">${(getAddonsTotal() * guestCount).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddonsSection;
